import { Card, Spin, Tooltip } from 'antd';
import { useTheme } from '../../contexts/useTheme';
import { useEventSelection } from '../../contexts/useEventSelection';
import { useStationObservations } from '../../contexts/useStationObservations';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ObservationTimestampProps {
  compact?: boolean;
}

export const ObservationTimestamp: React.FC<ObservationTimestampProps> = ({ compact = false }) => {
  const { theme } = useTheme();
  const { selectedEvent } = useEventSelection();
  const { dataSource, currentWeather, loading, tioLoading } = useStationObservations();

  const isLoading = dataSource === 'stations' ? loading : tioLoading;

  const getDisplayDateTime = () => {
    if (isLoading) return { date: '', time: '...', fullDate: '' };
    const ts = currentWeather.timestamp;
    if (!ts) return { date: '--', time: '--', fullDate: '--' };

    const eventTimezone = selectedEvent?.circuit?.time_zone || 'UTC';
    const dt = dayjs(ts).tz(eventTimezone);

    return {
      date: dt.format('DD MMM YYYY'),
      time: dt.format('HH:mm'),
      fullDate: dt.format('ddd, DD MMM YYYY'),
    };
  };

  const { date, time } = getDisplayDateTime();

  const label = dataSource === 'tio-api' ? 'Forecast Time' : 'Observation Time';

  const card = (
    <Card
      size="small"
      style={{
        background: theme === 'dark' ? '#1f1f1f' : '#f5f5f5',
        border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #d9d9d9',
        minWidth: compact ? '80px' : '140px',
        height: compact ? '32px' : '48px',
      }}
      styles={{ body: { padding: compact ? '2px 6px' : '6px 12px', height: '100%' } }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        {!compact && (
          <div
            style={{
              fontSize: '10px',
              fontWeight: 500,
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
              marginBottom: '2px',
              textAlign: 'center',
              lineHeight: '1.2',
            }}
          >
            {label}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
          <div
            style={{
              fontSize: compact ? '12px' : '16px',
              fontWeight: 600,
              color: theme === 'dark' ? 'white' : 'black',
              lineHeight: '1',
            }}
          >
            {time}
          </div>
          {isLoading && <Spin size="small" />}
        </div>
        {!compact && (
          <div
            style={{
              fontSize: '10px',
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
              marginTop: '3px',
              lineHeight: '1',
            }}
          >
            {date}
          </div>
        )}
      </div>
    </Card>
  );

  return compact ? <Tooltip title={`${label}: ${date}`}>{card}</Tooltip> : card;
};

